import { BlurEvent, ChangeEvent, Checkbox, ClickEvent, FocusEvent, Textbox } from "@mcleod/components";
import { AutogenLayoutMobileCommExAppControlDriver } from "./autogen/AutogenLayoutMobileCommExAppControlDriver";
import { MobileCommExAppControlHelper } from "./MobileCommExAppControlBase";
import { MobileCommFieldValidator } from "./MobileCommFieldValidator";

/**
 * Form for managing mobile communication configurations and field layouts.
 * Handles validation of field index values against available form fields.
 */
export class MobileCommExAppControlDriver extends AutogenLayoutMobileCommExAppControlDriver {

    private static readonly FIELD_TEXTBOX_IDS = [
        "textFieldOrderNumber",
        "textFieldAccepted",
        "textFieldDriverOrTractor",
        "textFieldTimeField",
        "textFieldSlipSeatDriverOrTractor",
        "textFieldTimeOffDate",
        "textFieldTimeOffDateField",
        "textFieldTimeOffTime",
        "textFieldTimeOffTimeField",
        "textFieldAvailableDate",
        "textFieldAvailableDateField",
        "textFieldAvailableTime",
        "textFieldAvailableTimeField",
        "textFieldDriverRemarks",
        "textFieldDriverRemarksField",
        "textFieldCallinRemarksField",
        "textFieldCallinRemarks"
    ];

    private static readonly VALIDATION_MESSAGE = "Both Date and Time are required.";

    private get driverEventFields(): Textbox[] {
        return [
            this.textFieldTimeOffDate,
            this.textFieldTimeOffTime,
            this.textFieldAvailableDate,
            this.textFieldAvailableTime
        ];
    }

    /**
     * Validates all field index textboxes to ensure values are within valid range.
     * @returns true if all validations pass, false otherwise
     */
    public validateAllFieldIndices(): boolean {
        return MobileCommFieldValidator.validateAllFieldIndices(
            this,
            MobileCommExAppControlDriver.FIELD_TEXTBOX_IDS,
            () => MobileCommFieldValidator.getMaxRowIndex(this.owner)
        );
    }

    /**
     * Event handler for textbox blur events.
     * Validates the field index value and displays inline warning if invalid.
     * @param event - the blur event
     */
    textboxFieldValidateOnBlur(event: BlurEvent): void {
        const textbox = event.target as Textbox;
        if (!event.changedWhileFocused) {
            return;
        }

        MobileCommFieldValidator.validateFieldOnBlur(
            textbox,
            this.owner.textboxToCheckboxMap.get(textbox.id),
            this.owner.findTextboxGroup(textbox.id),
            () => MobileCommFieldValidator.getMaxRowIndex(this.owner)
        );
    }

    /**
     * Event handler for component focus events.
     * Highlights the corresponding row in the form builder when a field textbox receives focus.
     * @param event - the focus event
     */
    componentOnFocus(event: FocusEvent): void {
        this.highlightFormField(event);
    }

    /**
     * Event handler for textbox change events.
     * Highlights the corresponding row in the form builder when a field textbox changes.
     * @param event - the change event
     */
    textboxOnChange(event: ChangeEvent): void {
        this.highlightFormField(event);
    }

    checkboxUpdatePtaDateOnClick(event: ClickEvent): void {
        const checkbox = event.target as Checkbox;
        MobileCommFieldValidator.handleDateTimeCheckboxUpdate(
            checkbox,
            this.textFieldOrderNumber,
            this.textFieldAccepted
        );
    }

    checkboxAddDriverEventOnClick(event: ClickEvent): void {
        const checkbox = event.target as Checkbox;

        if (!checkbox.checked) {
            this.clearValidation(this.driverEventFields);
        } else {
            this.validateRequiredFields(this.driverEventFields);
        }
    }

    private highlightFormField(event: FocusEvent | ChangeEvent): void {
        MobileCommExAppControlHelper.componentOnFocus(event, this, MobileCommExAppControlDriver.FIELD_TEXTBOX_IDS);
    }

    private clearValidation(textFields: Textbox[]): void {
        textFields.forEach(field => {
            field.validationWarning = null;
            field.required = false;
        });
    }

    private validateRequiredFields(textFields: Textbox[]): void {
        textFields.forEach(field => {
            if (!field.text) {
                field.required = true;
                field.validationWarning = MobileCommExAppControlDriver.VALIDATION_MESSAGE;
            }
        });
    }
}
